const currencyFormatters = new Map<string, Intl.NumberFormat>()

const getCurrencyFormatter = (currency: string, fractionDigits: number): Intl.NumberFormat => {
    const key = `${currency}:${fractionDigits}`
    const cached = currencyFormatters.get(key)
    if (cached) return cached
    let formatter: Intl.NumberFormat
    try {
        formatter = new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency,
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        })
    }
    catch {
        formatter = new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        })
    }
    currencyFormatters.set(key, formatter)
    return formatter
}

export const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100

export const formatCurrency = (amount: number, currency = 'INR'): string => {
    const value = roundMoney(Number.isFinite(amount) ? amount : 0)
    return getCurrencyFormatter(currency, Number.isInteger(value) ? 0 : 2).format(value)
}

const compactFormatter = new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 })

export const formatCompactNumber = (value: number): string => compactFormatter.format(value)

export const formatCompactCurrency = (amount: number, currency = 'INR'): string => {
    const symbol = getCurrencyFormatter(currency, 0).formatToParts(0).find(part => part.type === 'currency')?.value ?? ''
    return `${symbol}${compactFormatter.format(amount)}`
}

const numberFormatter = new Intl.NumberFormat('en-IN')

export const formatNumber = (value: number): string => numberFormatter.format(value)

export const formatPercent = (value: number): string => `${Math.round(value)}%`

// Phone numbers are stored as 10 digit Indian mobile numbers without the country code.
export const normalizePhone = (value: string): string => {
    let digits = value.replace(/\D/g, '')
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2)
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
    return digits
}

export const isValidPhone = (value: string): boolean => /^[6-9]\d{9}$/.test(normalizePhone(value))

export const formatPhone = (value: string, countryCode = '91'): string => {
    const digits = normalizePhone(value)
    if (digits.length !== 10) return value
    return `+${countryCode} ${digits.slice(0, 5)} ${digits.slice(5)}`
}

export const initials = (name: string): string => {
    const parts = name.replace(/^(dr|mr|mrs|ms)\.?\s+/i, '').split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string => {
    return `${count} ${count === 1 ? singular : plural}`
}

export const createId = (prefix: string): string => {
    const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
    return `${prefix}_${random}`
}

export const slugify = (value: string): string => {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}
